import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';
import {
  Tunnel,
  TunnelPackage,
  TunnelPackagePayload,
  TunnelPayload,
  TunnelSettings,
} from '../models/tunnel.model';
import { mapTunnelSettingsFromApi } from '../utils/tunnel.util';

/**
 * Super-admin tunnel settings of one location (CWS-1921, contract §6):
 * the tunnel and its tunnel wash packages.
 */
@Injectable({
  providedIn: 'root',
})
export class TunnelService {
  private readonly apiService = inject(ApiService);

  private tunnelPath(companyId: string, locationId: string, suffix = ''): string {
    return `admin/super-admin/companies/${companyId}/locations/${locationId}/tunnel${suffix}`;
  }

  private logged<T>(label: string) {
    return catchError<T, Observable<never>>(error => {
      console.error(`Error ${label}:`, error);
      return throwError(() => error);
    });
  }

  getTunnelSettings(
    companyId: string,
    locationId: string
  ): Observable<ApiResponse<TunnelSettings>> {
    return this.apiService
      .getWithSuperAdminToken<TunnelSettings>(this.tunnelPath(companyId, locationId))
      .pipe(
        map(response => ({
          ...response,
          data: response?.status ? mapTunnelSettingsFromApi(response.data) : response?.data,
        })),
        this.logged('fetching tunnel settings')
      );
  }

  saveTunnel(
    companyId: string,
    locationId: string,
    body: TunnelPayload
  ): Observable<ApiResponse<Tunnel>> {
    return this.apiService
      .putWithSuperAdminToken<Tunnel>(this.tunnelPath(companyId, locationId), body)
      .pipe(this.logged('saving tunnel'));
  }

  deleteTunnel(companyId: string, locationId: string): Observable<ApiResponse<unknown>> {
    return this.apiService
      .deleteWithSuperAdminToken(this.tunnelPath(companyId, locationId))
      .pipe(this.logged('deleting tunnel'));
  }

  createTunnelPackage(
    companyId: string,
    locationId: string,
    body: TunnelPackagePayload
  ): Observable<ApiResponse<TunnelPackage>> {
    return this.apiService
      .postWithSuperAdminToken<TunnelPackage>(
        this.tunnelPath(companyId, locationId, '/packages'),
        body
      )
      .pipe(this.logged('creating tunnel wash package'));
  }

  updateTunnelPackage(
    companyId: string,
    locationId: string,
    tunnelPackageId: string,
    body: TunnelPackagePayload
  ): Observable<ApiResponse<TunnelPackage>> {
    return this.apiService
      .patchWithSuperAdminToken<TunnelPackage>(
        this.tunnelPath(companyId, locationId, `/packages/${tunnelPackageId}`),
        body
      )
      .pipe(this.logged('updating tunnel wash package'));
  }

  deleteTunnelPackage(
    companyId: string,
    locationId: string,
    tunnelPackageId: string
  ): Observable<ApiResponse<unknown>> {
    return this.apiService
      .deleteWithSuperAdminToken(
        this.tunnelPath(companyId, locationId, `/packages/${tunnelPackageId}`)
      )
      .pipe(this.logged('deleting tunnel wash package'));
  }
}
